export type ImageOptimizationOptions = Readonly<{
  maxDimension?: number;
  quality?: number;
}>;

export async function optimizeImageFile(
  file: File,
  { maxDimension = 2048, quality = 0.82 }: ImageOptimizationOptions = {},
): Promise<File> {
  if (!file.type.startsWith('image/') || file.type === 'image/svg+xml') return file;
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) return file;
    context.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();
    const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, outputType, quality),
    );
    if (!blob || blob.size >= file.size) return file;
    const extension = outputType === 'image/png' ? '.png' : '.jpg';
    const name = `${file.name.replace(/\.[^.]+$/, '')}${extension}`;
    return new File([blob], name, { type: outputType, lastModified: file.lastModified });
  } catch {
    return file;
  }
}
